/**
 * Tree utilities for project subfolders and item grouping.
 */

/**
 * Convert the flat subfolders array (each with _id and parentId) into a
 * nested tree. rootId is the project folder ID — direct children of the
 * project root are the top-level nodes.
 */
export function buildTree(subfolders, rootId) {
    const rootIdStr = String(rootId);
    const byParent = {};
    for (const sub of subfolders) {
        const pid = String(sub.parentId);
        if (!byParent[pid]) byParent[pid] = [];
        byParent[pid].push(sub);
    }
    function children(id) {
        return (byParent[id] || []).map((sub) => ({
            ...sub,
            children: children(String(sub._id)),
        }));
    }
    return children(rootIdStr);
}

/**
 * Collect all folder IDs present in the tree (including root) into a flat
 * array of { id, name } for the move-to dropdown.
 */
export function flattenFolderOptions(rootFolderId, subfoldersTree) {
    const opts = [{ id: rootFolderId, name: '(project root)' }];
    function walk(nodes) {
        for (const node of nodes) {
            opts.push({ id: String(node._id), name: node.name });
            walk(node.children || []);
        }
    }
    walk(subfoldersTree);
    return opts;
}

/**
 * Collect all descendant folder IDs of a given node (by ID) in the tree.
 * Used to filter out invalid move-to destinations for subfolder moves.
 */
export function getDescendantIds(nodeId, subfoldersTree) {
    const ids = new Set();
    function find(nodes) {
        for (const node of nodes) {
            if (String(node._id) === nodeId) {
                collectAll(node.children || []);
                return true;
            }
            if (find(node.children || [])) return true;
        }
        return false;
    }
    function collectAll(nodes) {
        for (const node of nodes) {
            ids.add(String(node._id));
            collectAll(node.children || []);
        }
    }
    find(subfoldersTree);
    return ids;
}

/**
 * Build a flat map of subfolderId → items for efficient lookup.
 */
export function groupItemsBySubfolder(items) {
    const groups = {};
    for (const item of items) {
        const key = item._subfolderId ?? null;
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
    }
    return groups;
}
